/**
 * Generates a GeoJSON URL for geojson.io with a point and circle
 * @param lat - Latitude
 * @param lng - Longitude
 * @param radiusInMeters - Radius in meters
 * @returns URL string for geojson.io
 */
export const generateGeoJsonUrl = (lat: number, lng: number, radiusInMeters: number, name = 'GPS Location'): string => {
  const circleCoords = createCircle(lat, lng, radiusInMeters)
  const roundedRadius = Math.round(radiusInMeters)

  const geoJsonData = {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        properties: {
          name,
          description: `Range: ${roundedRadius} meters`,
        },
        geometry: {
          type: 'Point',
          coordinates: [lng, lat],
        },
      },
      {
        type: 'Feature',
        properties: {
          name: 'GPS Range Area',
          description: `${roundedRadius} meter radius`,
          stroke: '#ff0000',
          'stroke-width': 2,
          'stroke-opacity': 0.8,
          fill: '#ff0000',
          'fill-opacity': 0.1,
        },
        geometry: {
          type: 'Polygon',
          coordinates: [circleCoords],
        },
      },
    ],
  }

  const encodedGeoJson = encodeURIComponent(JSON.stringify(geoJsonData))
  return `https://geojson.io/#data=data:application/json,${encodedGeoJson}&map=12/${lat}/${lng}`
}

const createCircle = (
  centerLat: number,
  centerLng: number,
  radiusInMeters: number,
  points = 64,
): [number, number][] => {
  const coords: [number, number][] = []
  const earthRadius = 6371000

  for (let i = 0; i <= points; i++) {
    const angle = (i * 360) / points
    const angleRad = (angle * Math.PI) / 180

    const lat = Math.asin(
      Math.sin((centerLat * Math.PI) / 180) * Math.cos(radiusInMeters / earthRadius) +
        Math.cos((centerLat * Math.PI) / 180) * Math.sin(radiusInMeters / earthRadius) * Math.cos(angleRad),
    )

    const lng =
      (centerLng * Math.PI) / 180 +
      Math.atan2(
        Math.sin(angleRad) * Math.sin(radiusInMeters / earthRadius) * Math.cos((centerLat * Math.PI) / 180),
        Math.cos(radiusInMeters / earthRadius) - Math.sin((centerLat * Math.PI) / 180) * Math.sin(lat),
      )

    coords.push([(lng * 180) / Math.PI, (lat * 180) / Math.PI])
  }

  return coords
}
